import apiClient from './apiClient';
import {
  Interview,
  InterviewFeedback,
} from '../types/interviews';
import {
  RecommendedItem,
  ApiResponse as RecommendationApiResponse,
  RecommendQueryDto,
  RecommendableEntityType
} from '../types/recommendations';
import { Series } from '../components/interviews/InterviewSeriesSection';

// Extend RecommendQueryDto with our frontend-specific properties
interface ExtendedRecommendQueryDto extends Omit<RecommendQueryDto, 'itemTypeToRecommend'> {
  forceRefresh?: boolean;
  timestamp?: number;
}

// Simple cache provider implementation
class CacheProvider {
  private cache: Map<string, { data: any; expiry: number }> = new Map();
  private inFlightRequests: Map<string, Promise<any>> = new Map();

  get(key: string): any {
    const item = this.cache.get(key);
    if (!item) return null;

    // Return null if item has expired
    if (item.expiry < Date.now()) {
      this.cache.delete(key);
      return null;
    }

    return item.data;
  }

  set(key: string, data: any, ttlSeconds: number): void {
    const expiry = Date.now() + (ttlSeconds * 1000);
    this.cache.set(key, { data, expiry });
  }

  clear(): void {
    this.cache.clear();
    this.inFlightRequests.clear();
  }

  delete(key: string): void {
    this.cache.delete(key);
    this.inFlightRequests.delete(key);
  }

  /**
   * Clear all cache entries whose keys start with the given pattern
   */
  clearPattern(pattern: string): void {
    const keysToDelete: string[] = [];

    // First collect keys to avoid modifying map during iteration
    for (const key of this.cache.keys()) {
      if (key.startsWith(pattern)) {
        keysToDelete.push(key);
      }
    }

    // Then delete each matching key
    for (const key of keysToDelete) {
      this.cache.delete(key);
    }

    // Also clear any in-flight requests matching the pattern
    const requestKeysToDelete: string[] = [];
    for (const key of this.inFlightRequests.keys()) {
      if (key.startsWith(pattern)) {
        requestKeysToDelete.push(key);
      }
    }

    for (const key of requestKeysToDelete) {
      this.inFlightRequests.delete(key);
    }
  }

  /**
   * Store an in-flight request to prevent duplicate requests
   */
  setInFlightRequest(key: string, promise: Promise<any>): Promise<any> {
    this.inFlightRequests.set(key, promise);

    // Clean up after the promise resolves or rejects
    promise.finally(() => {
      if (this.inFlightRequests.get(key) === promise) {
        this.inFlightRequests.delete(key);
      }
    });

    return promise;
  }

  /**
   * Get an in-flight request if it exists
   */
  getInFlightRequest(key: string): Promise<any> | null {
    return this.inFlightRequests.get(key) || null;
  }
}

export interface InterviewRequest {
  topicId: string;
  jobRole?: string;
  company?: string;
  duration?: number;
  language?: string;
}

export interface InterviewSeriesRequest {
  seriesId: string;
  roundId: string;
}

export interface CustomInterviewFormData {
  jobRole: string;
  company: string;
  roundType: string;
  customRoundType?: string;
  jobDescription: string;
  language: string;
  difficulty: number;
  scheduledAt?: Date | string;
  languageId?: string;
}

export interface UserInterviewProgress {
  interview: string;
  status: 'not_started' | 'in_progress' | 'completed' | 'cancelled' | 'expired' | 'scheduled' | 'locked';
  language: string;
  userInterviewId: string;
  score?: number;
  minutesUsed?: number;
  feedback?: InterviewFeedback;
  startedAt?: string;
  completedAt?: string;
  locked: boolean;
  
  // Interview details populated by backend
  title?: string;
  description?: string;
  company?: string;
  companyId?: string;
  role?: string;
  tags?: string[];
  durationMinutes?: number;
  difficulty?: number;
  features?: string[];
  isActive?: boolean;
  voicePack?: string;
  characterGender?: string;
  interviewPrompt?: string;
  createdAt?: string;
  updatedAt?: string;
  userFeedbackRating?: string | null;
  isCustom?: boolean;
}

export interface SeriesDataResponse {
  _id: string;
  name: string;
  description: string;
  company: string;
  companyId?: string;
  companyLogo?: string;
  companyLogoUrl?: string;
  role?: string;
  jobRoleId?: string;
  jobRoleTitle?: string;
  roleDescription?: string;
  interviews: RecommendedInterview[];
  difficulty: number;
  enrollmentCount: number;
  totalTimeMinutes: number;
  passScore?: number;
  requiredPassScore?: number;
  isActive: boolean;
  createdAt: string | object;
  updatedAt: string | object;
  totalRounds: number;
  matchScore?: number; // Match score percentage for the current user
  match_score?: number; // Match score percentage for the current user
  preparationTimeWeeks?: string;
  successRate?: number; // Number of users who have completed the series
  userProgress?: {
    totalInterviews: number;
    completed: number;
    inProgress: number;
    pending: number;
    completionPercentage: number;
    userInterviews: UserInterviewProgress[]
  }
}

// Define interview types
export interface InterviewQuestion {
  _id: string;
  text: string;
  description?: string;
  difficulty?: number;
  expectedDuration?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface ApiResponse<T> {
  statusCode: number;
  message: string;
  data: T;
}

// Add this interface to represent the interview data structure
export interface UserInterview {
  _id: string; // This is the userInterviewId which means user's scheduled interview id
  user: string;
  interview: {
    _id: string;
    title: string;
    description: string;
    companyId?: string;
    company?: string;
    companyLogo?: string;
    role?: string;
    tags: string[];
    score?: number;
    language?: string;
    durationMinutes?: number;
    difficulty?: number;
    features?: string[];
    isActive?: boolean;
    voicePack?: string;
    characterGender?: string;
    interviewPrompt?: string;
    createdAt: string;
    updatedAt: string;
    // Custom interview properties
    isCustom?: boolean;
    jobDescription?: string;
    roundType?: string;
  } | null; // Allow null for custom interviews that reference customInterview template
  series?: {
    _id: string;
    name: string;
    company?: string;
    companyId?: string;
    companyLogo?: string;
    companyLogoUrl?: string;
    description?: string;
    enrollmentCount?: number;
    totalTimeMinutes?: number;
    role?: string;
    jobRoleId?: string;
    jobRoleTitle?: string;
    roleDescription?: string;
    difficulty?: number;
    passScore?: number;
    requiredPassScore?: number;
    isActive?: boolean;
  } | null;
  status: 'not_started' | 'in_progress' | 'completed' | 'cancelled' | 'expired' | 'scheduled' | 'locked';
  scheduledAt: string;
  startedAt?: string;
  completedAt?: string;
  minutesUsed: number;
  feedback: InterviewFeedback | undefined;
  userFeedbackRating?: string | null; // User's like/dislike rating for the feedback
  createdAt: string;
  updatedAt: string;
  language?: string;
  languageId?: string;
  languageName?: string;
  // Flag to identify if this is a custom interview at the UserInterview level
  isCustomInterview?: boolean;
  // Reference to the custom interview template (for custom interviews)
  customInterview?: string;
  metadata?: {
    calendarEventId?: string;
    calendarEventLink?: string;
    [key: string]: any;
  };
}

/**
 * @deprecated Old type for recommended interviews. Use RecommendedItem from '../types/recommendations' instead.
 */
export interface RecommendedInterview {
  _id: string;
  title: string;
  description: string;
  company?: string;
  companyId?: string;
  companyLogo?: string;
  role?: string;
  jobRoleId?: string;
  jobRoleTitle?: string;
  tags?: string[];
  durationMinutes?: number;
  difficulty?: number;
  features?: string[];
  isActive?: boolean;
  status?: string;
  voicePack?: string;
  characterGender?: string;
  interviewPrompt?: string;
  languageId?: string;
  language?: string;
  languageName?: string;
  createdAt?: any;
  updatedAt?: any;
  feedback?: InterviewFeedback;
  matchScore?: number;
  scheduledAt?: string;
  userInterviewId?: string;
  minutesUsed?: number;
}

class InterviewService {
  private cache: CacheProvider;

  constructor() {
    this.cache = new CacheProvider();
  }

  /**
   * Clear all cached recommendations for a specific user
   * @param userId The user ID whose recommendation caches should be cleared
   */
  clearUserRecommendationCaches(userId: string): void {
    if (!userId) return;

    // Clear patterns that might contain user-specific recommendations
    this.cache.clearPattern(`recommended_interviews_`);
    this.cache.clearPattern(`recommended_series_`);
    this.cache.clearPattern(`search_interviews_`);
    this.cache.clearPattern(`user_interview_history`);

    console.log(`[InterviewService] Cleared frontend recommendation caches for user ${userId}`);
  }

  /**
   * Clear interview history cache to force fresh data fetch
   */
  clearInterviewHistoryCache(): void {
    this.cache.clearPattern('user_interview_history');
    console.log('Cleared interview history cache');
  }

  /**
   * Sort interviews by scheduledAt date (closest first)
   * @param interviews Array of interviews to sort
   * @returns Sorted array of interviews
   */
  sortInterviewsByScheduledDate<T extends { scheduledAt: string }>(interviews: T[]): T[] {
    return interviews.sort((a, b) => new Date(b.scheduledAt).getTime() - new Date(a.scheduledAt).getTime());
  }

  /**
   * Get details for a specific interview series
   * @param seriesId Series ID
   * @returns Series details
   */
  async getSeriesDetails(seriesId: string): Promise<SeriesDataResponse> {
    const cacheKey = `series_details_${seriesId}`;
    const cachedData = this.cache.get(cacheKey) as SeriesDataResponse | null;
    if (cachedData) return cachedData;

    const inFlight = this.cache.getInFlightRequest(cacheKey) as Promise<SeriesDataResponse> | null;
    if (inFlight) return inFlight;

    // apiClient.get<T> will return T after normalization.
    // If server sends RecommendationApiResponse<SeriesDataResponse>, 
    // normalizeApiResponse extracts SeriesDataResponse and adds _response.
    // So, T here should be SeriesDataResponse.
    const requestPromise = apiClient.get<SeriesDataResponse>(`/interview-series/${seriesId}`)
      .then(seriesData => { // seriesData is SeriesDataResponse (possibly with _response)
        if (!seriesData || !seriesData._id) { // Validate the actual data
          console.error('Invalid API response structure for series details:', seriesData);
          throw new Error('Invalid API response structure for series details or missing _id');
        }
        // The seriesData is what we want to cache and return.
        this.cache.set(cacheKey, seriesData, 300);
        return seriesData;
      })
      .catch(error => {
        console.error(`Error fetching series details for ${seriesId}:`, error);
        this.cache.delete(cacheKey); // Ensure cache is cleared on error
        throw error;
      });
    return this.cache.setInFlightRequest(cacheKey, requestPromise);
  }

  /**
   * Enroll in an interview series
   * @param seriesId Series ID
   * @returns Enrollment response or throws error if failed
   */
  async enrollInSeries(seriesId: string): Promise<any> {
    try {
      const response = await apiClient.post<ApiResponse<any>>(`/user-interviews/series/${seriesId}/enroll`);

      console.log('interviewService: Series enrollment:', response);

      // Handle different response formats
      if (response && typeof response === 'object') {
        // Handle case where api returns the whole object with data property
        if ('data' in response && response.data && 'data' in response.data) {
          return response.data.data;
        }

        return response;
      }

      return response;
    } catch (error: any) {
      console.error('Error enrolling in series:', error);
      
      // Extract meaningful error message
      let errorMessage = 'Failed to enroll in series';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      // Throw error instead of returning null
      throw new Error(errorMessage);
    }
  }

  async getRecommendedInterviews(query: ExtendedRecommendQueryDto): Promise<RecommendationApiResponse<RecommendedItem[]>> {
    const cacheKey = `recommended_interviews_query_${JSON.stringify(query)}`;
    const cachedData = this.cache.get(cacheKey) as RecommendationApiResponse<RecommendedItem[]> | null;
    if (cachedData) return cachedData;

    const inFlight = this.cache.getInFlightRequest(cacheKey) as Promise<RecommendationApiResponse<RecommendedItem[]>> | null;
    if (inFlight) return inFlight;

    const requestBody: RecommendQueryDto = {
      ...query,
      itemTypeToRecommend: RecommendableEntityType.INTERVIEW,
    };

    // T for apiClient.post is RecommendedItem[] (the actual data part of the wrapper)
    // normalizeApiResponse will return (RecommendedItem[] & { _response: { statusCode, message } })
    const requestPromise = apiClient.post<RecommendedItem[] & { _response: { statusCode: number, message: string } }>('/recommendations/interviews', requestBody)
      .then(normalizedResponse => {

        if (!normalizedResponse || !normalizedResponse._response) { // Check for _response property
          console.error('Invalid API response: Missing _response property in normalizedResponse for recommended interviews:', normalizedResponse);
        }

        const recommendedItems: RecommendedItem[] = [];
        for (const key in normalizedResponse) {
          if (key !== '_response' && normalizedResponse.hasOwnProperty(key) && !isNaN(Number(key))) {
            const item = normalizedResponse[key as any] as RecommendedItem;
            
            // Map metadata properties to top level for easier access
            const processedItem = {
              ...item,
              // Extract commonly used properties from metadata to top level
              company: item.metadata?.company || item.company,
              companyLogo: item.metadata?.companyLogo || item.companyLogo,
              companyId: item.metadata?.companyId || item.companyId,
              role: item.metadata?.role || item.role,
              jobRoleId: item.metadata?.jobRoleId || item.jobRoleId,
              jobRoleTitle: item.metadata?.jobRoleTitle || item.jobRoleTitle,
              difficulty: item.metadata?.difficulty || item.difficulty,
              durationMinutes: item.metadata?.durationMinutes || item.durationMinutes,
              tags: item.metadata?.tags,
              features: item.metadata?.features,
              isActive: item.metadata?.isActive !== undefined ? item.metadata.isActive : item.isActive,
              voicePack: item.metadata?.voicePack,
              characterGender: item.metadata?.characterGender
            };
            
            recommendedItems.push(processedItem);
          }
        }

        console.log('[InterviewService] Processed recommended items with metadata mapping:', recommendedItems.length > 0 ? recommendedItems[0] : 'none');

        const reconstructedResponse: RecommendationApiResponse<RecommendedItem[]> = {
          statusCode: normalizedResponse._response?.statusCode,
          message: normalizedResponse._response?.message,
          data: recommendedItems // Use the processed items
        };

        this.cache.set(cacheKey, reconstructedResponse, 300);
        return reconstructedResponse;
      })
      .catch(error => {
        console.error('Error fetching recommended interviews:', error);
        this.cache.delete(cacheKey);
        throw error;
      });
    return this.cache.setInFlightRequest(cacheKey, requestPromise);
  }

  /**
   * Perform hybrid search combining text and vector search
   * @param searchQuery The text to search for
   * @param userId User ID for personalized results
   * @param limit Maximum number of results to return
   * @param forceRefresh Optional flag to force a fresh API call, bypassing cache
   * @returns A promise that resolves to hybrid search results with text matches first
   */
  async hybridSearchInterviews(searchQuery: string, userId?: string, limit?: number, forceRefresh = false): Promise<{
    textResults: RecommendedItem[];
    vectorResults: RecommendedItem[];
    combinedResults: RecommendedItem[];
    searchMetadata: {
      textMatches: number;
      vectorMatches: number;
      totalResults: number;
      searchQuery: string;
      searchType: 'hybrid';
    };
  }> {
    // Build the query parameters
    const query: ExtendedRecommendQueryDto = {
      queryText: searchQuery?.trim() || '',
      userId: userId,
      limit: limit || 20,
      forceRefresh: forceRefresh
    };

    // Create a unique cache key based on the search parameters
    const cacheKey = `hybrid_search_interviews_${JSON.stringify(query)}`;

    // Check cache first to avoid redundant API calls, but skip if forceRefresh is true
    if (!forceRefresh) {
      const cachedData = this.cache.get(cacheKey);
      if (cachedData) return cachedData;

      // Check if there's an in-flight request with the same parameters
      const inFlight = this.cache.getInFlightRequest(cacheKey);
      if (inFlight) return inFlight;
    } else if (userId) {
      // Clear any existing cache for this user's recommendations
      this.clearUserRecommendationCaches(userId);
    }

    // Prepare the request body
    const requestBody: RecommendQueryDto = {
      ...query,
      itemTypeToRecommend: RecommendableEntityType.INTERVIEW,
    };

    // Make the API request to the hybrid search endpoint
    const requestPromise = apiClient.post<any>('/recommendations/interviews/hybrid-search', requestBody)
      .then(response => {
        console.log('Hybrid search response:', response);
        
        // Process the hybrid search results
        const hybridResult = {
          textResults: response.textResults || [],
          vectorResults: response.vectorResults || [],
          combinedResults: response.combinedResults || [],
          searchMetadata: response.searchMetadata || {
            textMatches: 0,
            vectorMatches: 0,
            totalResults: 0,
            searchQuery: searchQuery || '',
            searchType: 'hybrid' as const
          }
        };

        // Cache the result for 5 minutes (300 seconds)
        this.cache.set(cacheKey, hybridResult, 300);
        return hybridResult;
      })
      .catch(error => {
        console.error('Error performing hybrid search:', error);
        this.cache.delete(cacheKey);
        throw error;
      });

    // Register this as an in-flight request and return the promise
    return this.cache.setInFlightRequest(cacheKey, requestPromise);
  }

  /**
   * Search for interviews with match scores based on query text or user preferences
   * @param searchQuery The text to search for or empty string for user-based recommendations
   * @param userId Optional user ID for personalized results
   * @param limit Maximum number of results to return
   * @param forceRefresh Optional flag to force a fresh API call, bypassing cache
   * @returns A promise that resolves to an API response containing matched interviews with scores
   */
  async searchInterviews(searchQuery: string, userId?: string, limit?: number, forceRefresh = false): Promise<RecommendationApiResponse<RecommendedItem[]>> {
    // Build the query parameters with all the search criteria
    const query: ExtendedRecommendQueryDto = {};

    // Only include non-empty search terms
    if (searchQuery && searchQuery.trim() !== '') {
      query.queryText = searchQuery.trim();
    }

    // Include user ID if provided for personalized results
    if (userId) {
      query.userId = userId;
    }

    // Set result limit if provided
    if (limit && limit > 0) {
      query.limit = limit;
    }

    // Add force refresh flag if needed
    if (forceRefresh) {
      query.forceRefresh = true;
    }

    // Create a unique cache key based on the search parameters
    const cacheKey = `search_interviews_${JSON.stringify(query)}`;

    // Check cache first to avoid redundant API calls, but skip if forceRefresh is true
    if (!forceRefresh) {
      const cachedData = this.cache.get(cacheKey) as RecommendationApiResponse<RecommendedItem[]> | null;
      if (cachedData) return cachedData;

      // Check if there's an in-flight request with the same parameters
      const inFlight = this.cache.getInFlightRequest(cacheKey) as Promise<RecommendationApiResponse<RecommendedItem[]>> | null;
      if (inFlight) return inFlight;
    } else if (userId) {
      // Clear any existing cache for this user's recommendations
      this.clearUserRecommendationCaches(userId);
    }

    // Prepare the request body with the interview type
    const requestBody: RecommendQueryDto = {
      ...query,
      itemTypeToRecommend: RecommendableEntityType.INTERVIEW,
    };

    // Make the API request with proper error handling
    const requestPromise = apiClient.post<RecommendedItem[] & { _response: { statusCode: number, message: string } }>('/recommendations/interviews', requestBody)
      .then(normalizedResponse => {
        // Validate the response structure
        if (!normalizedResponse || !normalizedResponse._response) {
          console.error('Invalid API response structure for search interviews:', normalizedResponse);
          throw new Error('Invalid API response structure for search interviews');
        }

        // Extract the recommended items from the response
        const recommendedItems: RecommendedItem[] = [];
        for (const key in normalizedResponse) {
          if (key !== '_response' && normalizedResponse.hasOwnProperty(key) && !isNaN(Number(key))) {
            const item = normalizedResponse[key as any] as RecommendedItem;
            
            // Map metadata properties to top level for easier access
            const processedItem = {
              ...item,
              // Extract commonly used properties from metadata to top level
              company: item.metadata?.company || item.company,
              companyLogo: item.metadata?.companyLogo || item.companyLogo,
              companyId: item.metadata?.companyId || item.companyId,
              role: item.metadata?.role || item.role,
              jobRoleId: item.metadata?.jobRoleId || item.jobRoleId,
              jobRoleTitle: item.metadata?.jobRoleTitle || item.jobRoleTitle,
              difficulty: item.metadata?.difficulty || item.difficulty,
              durationMinutes: item.metadata?.durationMinutes || item.durationMinutes,
              tags: item.metadata?.tags,
              features: item.metadata?.features,
              isActive: item.metadata?.isActive !== undefined ? item.metadata.isActive : item.isActive,
              voicePack: item.metadata?.voicePack,
              characterGender: item.metadata?.characterGender
            };
            
            recommendedItems.push(processedItem);
          }
        }

        console.log('[InterviewService] Processed search items with metadata mapping:', recommendedItems.length > 0 ? recommendedItems[0] : 'none');

        // Reconstruct the API response with the extracted items
        const reconstructedResponse: RecommendationApiResponse<RecommendedItem[]> = {
          statusCode: normalizedResponse._response?.statusCode,
          message: normalizedResponse._response?.message,
          data: recommendedItems
        };

        // Cache the result for 5 minutes (300 seconds)
        this.cache.set(cacheKey, reconstructedResponse, 300);
        return reconstructedResponse;
      })
      .catch(error => {
        console.error('Error searching for interviews:', error);
        this.cache.delete(cacheKey);
        throw error;
      });

    // Register this as an in-flight request and return the promise
    return this.cache.setInFlightRequest(cacheKey, requestPromise);
  }

  async getRecommendedInterviewSeries(query: ExtendedRecommendQueryDto): Promise<RecommendationApiResponse<RecommendedItem[]>> {
    const cacheKey = `recommended_series_query_${JSON.stringify(query)}`;
    const cachedData = this.cache.get(cacheKey) as RecommendationApiResponse<RecommendedItem[]> | null;
    if (cachedData) return cachedData;

    const inFlight = this.cache.getInFlightRequest(cacheKey) as Promise<RecommendationApiResponse<RecommendedItem[]>> | null;
    if (inFlight) return inFlight;

    const requestBody: RecommendQueryDto = {
      ...query,
      itemTypeToRecommend: RecommendableEntityType.INTERVIEW_SERIES,
    };

    // Track the promise for in-flight cache
    // The 'response' here is the normalized one from apiClient
    const promise = apiClient.post<any>('/recommendations/interview-series', requestBody)
      .then(normalizedResponse => { // Renamed for clarity
        console.log('[interviewService] Raw NORMALIZED response from recommendations/interview-series:', JSON.stringify(normalizedResponse));

        let itemsArray: RecommendedItem[] = [];
        let responseStatusCode = 200;
        let responseMessage = 'Success';

        if (normalizedResponse && typeof normalizedResponse === 'object') {
          if (normalizedResponse._response && typeof normalizedResponse._response === 'object') {
            responseStatusCode = normalizedResponse._response?.statusCode || 200;
            responseMessage = normalizedResponse._response?.message || 'Success';
          }

          // Extract items from numeric keys
          itemsArray = Object.keys(normalizedResponse)
            .filter(key => !isNaN(Number(key))) // Check if the key is a number (0, 1, 2...)
            .map(key => normalizedResponse[key] as RecommendedItem)
            .filter(item => item && typeof item === 'object'); // Ensure it's a valid item

          console.log(`[interviewService] Extracted ${itemsArray.length} items from normalized response.`);
          if (itemsArray.length > 0) {
            // console.log('[interviewService] First extracted item:', JSON.stringify(itemsArray[0]));
            // console.log('[interviewService] First item score:', itemsArray[0].score, 'type:', typeof itemsArray[0].score);
          }
        } else {
          console.warn('[interviewService] Normalized response was not an object or was null/undefined.');
        }

        const apiResponse: RecommendationApiResponse<RecommendedItem[]> = {
          statusCode: responseStatusCode,
          message: responseMessage,
          data: itemsArray.map((item: RecommendedItem) => { // Process the extracted itemsArray
            const metadata = item.metadata || {};
            const scoreValue = typeof item.score === 'string'
              ? parseFloat(item.score)
              : (typeof item.score === 'number' ? item.score : 0);

            // console.log(`[interviewService] Processing item ${item.id}: original score=${item.score}, after conversion=${scoreValue}`);

            return {
              ...item,
              score: scoreValue,
              matchScore: scoreValue,
              match_score: scoreValue,
              company: metadata.company || metadata.companyName,
              interviews: metadata.interviews || [],
              companyId: metadata.companyId,
              totalRounds: metadata.interviews?.length || 0,
              preparationTimeWeeks: metadata.preparationTimeWeeks || 0,
              companyLogo: metadata.companyLogoUrl || metadata.companyLogo,
              role: metadata.role || metadata.jobRoleName,
              jobRoleId: metadata.jobRoleId,
              jobRoleTitle: metadata.jobRoleName,
              difficulty: metadata.difficulty || 3,
              durationMinutes: metadata.durationMinutes || metadata.totalTimeMinutes,
              totalTimeMinutes: metadata.totalTimeMinutes,
              isActive: metadata.isActive !== undefined ? metadata.isActive : true,
              passScore: metadata.passScore || metadata.requiredPassScore,
            };
          }),
        };

        if (apiResponse.data.length > 0) {
          // console.log('[interviewService] First PROCESSED recommendation item for series:', JSON.stringify(apiResponse.data[0]));
        }

        this.cache.set(cacheKey, apiResponse, 300);
        return apiResponse;
      })
      .catch(error => {
        console.error('[interviewService] Error fetching recommended interview series:', error);
        return {
          statusCode: error?.response?.status || error?.status || 500,
          message: error?.response?.data?.message || error?.message || 'Error fetching recommendations. Please upload a resume for personalized recommendations.',
          data: []
        };
      });

    this.cache.setInFlightRequest(cacheKey, promise);
    return promise;
  }

  /**
   * Search for interview series by query
   * @param searchQuery Text to search for in series title or description
   * @returns Matching interview series
   */
  async getSeriesBySearch(searchQuery: string): Promise<SeriesDataResponse[]> {
    try {
      console.log(`Searching for series with query: "${searchQuery}"`);
      const response = await apiClient.get<any>(`/interview-series?search=${encodeURIComponent(searchQuery)}`);

      console.log('Raw search response:', response);

      // Handle the response structure - the data might be directly in the response
      // or nested under a data property
      let seriesData: SeriesDataResponse[] = [];

      if (response && typeof response === 'object') {
        // Check if response has a data property
        if ('data' in response && Array.isArray(response.data)) {
          seriesData = response.data;
        } else if (Array.isArray(response)) {
          // Response is directly an array
          seriesData = response;
        } else {
          // Response might be an object with numbered properties (like {0: item1, 1: item2, ...})
          const keys = Object.keys(response).filter(key => key !== '_response' && !isNaN(Number(key)));
          if (keys.length > 0) {
            seriesData = keys.map(key => (response as any)[key]).filter(item => item && typeof item === 'object');
          }
        }
      }

      console.log('Processed series data:', seriesData);
      return seriesData;
    } catch (error) {
      console.error(`Error searching for series with query "${searchQuery}":`, error);
      return [];
    }
  }

  /**
   * Get all interview series
   * @param searchQuery Optional search query to filter series by name or description
   * @param forceRefresh Optional flag to force a fresh API call, bypassing cache
   * @returns List of interview series
   */
  async getInterviewSeriesList(searchQuery?: string | boolean, forceRefresh = false): Promise<SeriesDataResponse[]> {
    // Handle case where forceRefresh is passed as first parameter
    if (typeof searchQuery === 'boolean') {
      forceRefresh = searchQuery;
      searchQuery = undefined;
    }
    try {
      console.log('Fetching interview series with query:', searchQuery);

      // Prioritize search query if provided
      if (searchQuery && searchQuery.trim() !== '') {
        const searchResults = await this.getSeriesBySearch(searchQuery);
        if (Array.isArray(searchResults) && searchResults.length > 0) {
          console.log(`Found ${searchResults.length} series matching search: "${searchQuery}"`);
          return searchResults;
        } else {
          console.warn(`No series found matching search: "${searchQuery}"`);
        }
      }

      // Only use the recommendations endpoint - no fallback
      try {
        const recommendedResponse = await this.getRecommendedInterviewSeries({
          userId: 'current', // Special value indicating current logged-in user
          limit: 20, // Get more series to show
        });

        console.log("Recommended series response:", recommendedResponse);
        // Log the raw data structure
        console.log("Recommended series raw data structure:", {
          hasDataProperty: !!recommendedResponse?.data,
          dataIsArray: Array.isArray(recommendedResponse?.data),
          dataLength: recommendedResponse?.data?.length || 0,
          firstItemSample: recommendedResponse?.data?.[0] ? JSON.stringify(recommendedResponse.data[0]).substring(0, 200) + '...' : 'undefined'
        });

        // Check if we got results back
        if (recommendedResponse?.data?.length > 0) {
          console.log(`Converting ${recommendedResponse.data.length} recommended items to SeriesDataResponse format`);

          // Convert recommended items to SeriesDataResponse format
          const convertedSeries = recommendedResponse.data.map((item, index) => {
            // Log the exact format of each item we're converting
            // console.log(`Raw item ${index}:`, JSON.stringify(item));

            const metadata = item.metadata || {};
            // Ensure score is a number
            const scoreValue = typeof item.score === 'string'
              ? parseFloat(item.score)
              : (typeof item.score === 'number' ? item.score : 0);

            // console.log(`Converting item ${index} (${item.id}): score=${item.score}, type=${typeof item.score}, converted score=${scoreValue}`);

            const series: SeriesDataResponse = {
              _id: item.id,
              name: item.title || metadata.title || 'Untitled Series',
              description: item.description || metadata.description || '',
              company: metadata.company || metadata.companyName || '',
              companyId: metadata.companyId || '',
              companyLogo: metadata.companyLogo || metadata.companyLogoUrl || '',
              role: metadata.role || metadata.jobRoleName || '',
              jobRoleId: metadata.jobRoleId || '',
              jobRoleTitle: metadata.jobRoleTitle || metadata.jobRoleName || '',
              interviews: [], // Will be populated when series is viewed
              difficulty: metadata.difficulty || 3,
              enrollmentCount: metadata.enrollmentCount || 0,
              totalTimeMinutes: metadata.totalTimeMinutes || 0,
              preparationTimeWeeks: metadata.preparationTimeWeeks || '1 month',
              companyLogoUrl: metadata.companyLogoUrl || '',
              passScore: metadata.passScore || metadata.requiredPassScore || 0,
              isActive: metadata.isActive !== undefined ? metadata.isActive : true,
              createdAt: metadata.createdAt || new Date().toISOString(),
              updatedAt: metadata.updatedAt || new Date().toISOString(),
              // Set both matchScore and match_score to the same value
              matchScore: scoreValue,
              match_score: scoreValue,
              totalRounds: metadata.interviews?.length || 0,
            };

            // console.log(`Converted series ${index}: matchScore=${series.matchScore}, match_score=${series.match_score}`);
            return series;
          });

          // Log the first converted series
          if (convertedSeries.length > 0) {
            // console.log('First converted series:', JSON.stringify(convertedSeries[0]));
            // console.log('Series array type check:', {
            //   isArray: Array.isArray(convertedSeries),
            //   length: convertedSeries.length,
            //   firstItem_id: convertedSeries[0]._id,
            //   firstItemHasMatchScore: convertedSeries[0].hasOwnProperty('matchScore'),
            //   matchScoreValue: convertedSeries[0].matchScore
            // });
          }

          return convertedSeries;
        } else {
          console.log('No recommended series returned, returning empty array');
          return [];
        }
      } catch (recommendationError) {
        console.error('Error fetching recommended series:', recommendationError);
        console.log('Error with recommendation endpoint, returning empty array');
        return [];
      }
    } catch (error) {
      console.error('Error fetching interview series list:', error);
      return [];
    }
  }

  /**
   * Check if a user is enrolled in a specific interview series
   * @param seriesId Series ID to check enrollment for
   * @returns Boolean indicating if the user is enrolled or the full response object if it contains userProgress
   */
  async checkEnrollment(seriesId: string): Promise<boolean | any> {
    try {
      // Use the dedicated enrollment status endpoint
      const response = await apiClient.get<any>(`/user-interviews/me/series/${seriesId}/enrollment-status`);

      console.log('interviewService: Enrollment check:', response);

      // Handle different response formats
      if (response && typeof response === 'object') {
        // Check for the new enrollment status response format
        if ('isEnrolled' in response) {
          return Boolean(response.isEnrolled);
        }

        // Handle case where api returns the whole object with data property
        if ('data' in response && response.data && typeof response.data === 'object') {
          if ('isEnrolled' in response.data) {
            return Boolean(response.data.isEnrolled);
          }
        }
      }

      // For development purposes - check localStorage as fallback
      const enrolledSeries = localStorage.getItem('enrolled_series');
      if (enrolledSeries) {
        try {
          const parsed = JSON.parse(enrolledSeries);
          if (Array.isArray(parsed)) {
            return parsed.includes(seriesId);
          }
        } catch (e) {
          console.error('Error parsing enrolled series from localStorage:', e);
        }
      }

      return false;
    } catch (error) {
      console.error('Error checking enrollment:', error);

      // For development purposes - check localStorage as fallback
      const enrolledSeries = localStorage.getItem('enrolled_series');
      if (enrolledSeries) {
        try {
          const parsed = JSON.parse(enrolledSeries);
          if (Array.isArray(parsed)) {
            return parsed.includes(seriesId);
          }
        } catch (e) {
          console.error('Error parsing enrolled series from localStorage:', e);
        }
      }

      return false;
    }
  }

  /**
   * Get user interviews based on status - now supporting multiple status values
   * @param status Filter by interview status (not_started, started, completed, cancelled) or an array of statuses
   * @param fromDate Filter by date from which to get interviews e.g: 2024-06-01T00:00:00.000Z
   * @param toDate Filter by date to which to get interviews e.g: 2024-06-01T00:00:00.000Z
   * @param excludeCustom If true, excludes custom interviews from results
   * @returns List of user interviews
   */
  async getUserInterviews(status?: string | string[], fromDate?: string, toDate?: string, excludeCustom?: boolean): Promise<UserInterview[]> {
    try {
      // Check for multiple statuses
      if (Array.isArray(status)) {
        // Call this function recursively for each status and combine the results
        const promises = status.map(s => this.getUserInterviews(s, fromDate, toDate, excludeCustom));
        const results = await Promise.all(promises);
        return results.flat();
      }

      // Build the endpoint for a single status
      const queryParams = new URLSearchParams();
      if (status) queryParams.append('status', status.trim());
      if (fromDate) queryParams.append('from', fromDate);
      if (toDate) queryParams.append('to', toDate);
      if (excludeCustom) queryParams.append('excludeCustom', 'true');
      
      const endpoint = `/user-interviews/me${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

      const response = await apiClient.get<any>(endpoint);
      console.log('getUserInterviews response:', response);

      // Initialize empty array for the interviews
      let userInterviews: UserInterview[] = [];

      // Process the response based on its structure
      if (response === null || response === undefined) {
        console.warn('getUserInterviews: API response is null or undefined');
        return [];
      }

      // Handle the normalized API response format with _response property
      if (typeof response === 'object' && '_response' in response) {
        // This is the normalized format where items are stored with numeric keys
        const keys = Object.keys(response).filter(key => key !== '_response');

        if (keys.length === 0) {
          console.log('getUserInterviews: Response contains _response property but no items');
          return [];
        }

        // Check if we have numeric keys (0, 1, 2...)
        if (keys.some(key => !isNaN(Number(key)))) {
          const responseWithNumericKeys = response as Record<string, any>;
          const extractedInterviews = keys
            .filter(key => !isNaN(Number(key)))
            .map(key => responseWithNumericKeys[key])
            .filter(item => item && typeof item === 'object');

          if (extractedInterviews.length > 0) {
            console.log(`getUserInterviews: Extracted ${extractedInterviews.length} interviews from normalized response`);
            userInterviews = extractedInterviews;
          }
        }
      }
      // Check if response itself is an array
      else if (Array.isArray(response)) {
        userInterviews = response;
      }
      // Check if response.data is the array
      else if (response && typeof response === 'object' && 'data' in response) {
        if (Array.isArray(response.data)) {
          userInterviews = response.data;
        }
        // Check for response.data.data pattern
        else if (typeof response.data === 'object' && response.data !== null && 'data' in response.data) {
          if (Array.isArray(response.data.data)) {
            userInterviews = response.data.data;
          }
        }
      }

      // Log the result for debugging
      if (userInterviews.length > 0) {
        console.log(`getUserInterviews: Found ${userInterviews.length} interviews for status=${status || 'all'}`);
      } else {
        console.log(`getUserInterviews: No interviews found for status=${status || 'all'}`);
      }

      return userInterviews;
    } catch (error) {
      console.error('Error fetching user interviews:', error);
      return [];
    }
  }

  async getUserInterviewById(interviewId: string): Promise<UserInterview | null> {
    const response = await apiClient.get<ApiResponse<UserInterview>>(`/user-interviews/me/${interviewId}`);
    // Type guard to check if response has the expected structure
    if (response && typeof response === 'object' && 'data' in response &&
      response.data && typeof response.data === 'object' && 'data' in response.data) {
      const interview = response.data.data;
      return interview as UserInterview;
    }
    return null;
  }
  /**
   * Get upcoming interviews (not started and scheduled in the future)
   * @param filterActive If true, filters out inactive interviews
   * @returns List of upcoming interviews sorted by date (closest first)
   */
  async getUpcomingInterviews(filterActive: boolean = true): Promise<UserInterview[]> {
    // Exclude custom interviews from upcoming interviews as they are for immediate use
    const interviews = await this.getUserInterviews(['not_started', 'scheduled'], undefined, undefined, false);

    // Filter out interviews without proper structure
    const validInterviews = interviews.filter(interview => {
      if (!interview) return false;
      
      // For custom interviews, check if we have customInterview data or interview data
      if (interview.isCustomInterview === true) {
        // Custom interviews might have interview object OR customInterview reference
        return (interview.interview && typeof interview.interview === 'object') ||
               (interview.customInterview);
      }
      
      // For regular interviews, require interview object
      return interview.interview && typeof interview.interview === 'object';
    });

    // Custom interviews are already filtered out by the backend when excludeCustom=true
    // But we keep a client-side fallback for extra safety
    // However, we want to include scheduled custom interviews in upcoming interviews
    const nonCustomInterviews = validInterviews.filter(interview => {
      // For custom interviews, only include if they are scheduled (not just templates)
      if (interview.isCustomInterview === true || interview.interview?.isCustom === true) {
        // Include custom interviews that are scheduled
        return interview.status === 'scheduled' || interview.status === 'not_started';
      }
      
      // Include all non-custom interviews
      return true;
    });

    // Filter based on status and time logic
    const currentTime = new Date();
    const tenMinutesAgo = new Date(currentTime.getTime() - 10 * 60 * 1000);

    const filteredInterviews = filterActive
      ? nonCustomInterviews.filter(interview => {
          // Filter out inactive interviews
          if (interview.interview && interview.interview.isActive === false) {
            return false;
          }
          
          // For scheduled interviews, always include them (they have been deliberately scheduled)
          if (interview.status === 'scheduled') {
            return true;
          }
          
          // For not_started interviews, only include if they have a future scheduledAt
          if (interview.status === 'not_started') {
            return interview.scheduledAt && new Date(interview.scheduledAt) > tenMinutesAgo;
          }
          
          // Include any other valid status
          return true;
        })
      : nonCustomInterviews.filter(interview => {
          // For non-active filtering, still apply time logic for not_started interviews
          if (interview.status === 'scheduled') {
            return true;
          }
          
          if (interview.status === 'not_started') {
            return interview.scheduledAt && new Date(interview.scheduledAt) > tenMinutesAgo;
          }
          
          return true;
        });

    return this.sortInterviewsByScheduledDate(filteredInterviews);
  }

  /**
   * Schedule a new interview
   * @param interviewId ID of the interview to schedule
   * @param scheduledAt Date and time to schedule the interview
   * @param languageId Optional language ID for the interview
   * @param seriesId Optional series ID if this interview is part of a series
   * @returns Scheduled interview data
   */
  async scheduleInterview(interviewId: string, scheduledAt: Date | string, languageId?: string, seriesId?: string): Promise<UserInterview | null> {
    try {
      // Define a type guard function for UserInterview
      const isUserInterview = (obj: unknown): obj is UserInterview => {
        return obj !== null &&
          typeof obj === 'object' &&
          '_id' in obj &&
          'interview' in obj &&
          obj.interview !== null &&
          typeof obj.interview === 'object';
      };

      const payload: any = {
        interview: interviewId,
        scheduledAt: scheduledAt instanceof Date ? scheduledAt.toISOString() : scheduledAt,
        // Include languageId if provided
        ...(languageId && { languageId }),
        // Include series ID if provided
        ...(seriesId && { series: seriesId })
      };

      const response = await apiClient.post<ApiResponse<UserInterview> | UserInterview>('/user-interviews', payload);
      console.log('[interviewService] Received interview response:', response);

      // Check if response is direct UserInterview object (new format)
      if (response && typeof response === 'object' && '_id' in response && 'interview' in response) {
        console.log('[interviewService] Direct interview object received');
        if (isUserInterview(response)) {
          return response;
        }
      }

      // Handle old format (wrapped in ApiResponse)
      if (response && typeof response === 'object' && 'data' in response &&
        response.data && typeof response.data === 'object' && 'data' in response.data) {
        console.log('[interviewService] ApiResponse wrapped interview received');
        const interview = response.data.data;
        if (isUserInterview(interview)) {
          return interview;
        }
      }

      console.error('[interviewService] Unexpected response format:', response);
      return null;
    } catch (error) {
      console.error('Error scheduling interview:', error);
      throw error;
    }
  }

  /**
   * Reschedule an existing interview
   * @param userInterviewId ID of the user interview to reschedule
   * @param scheduledAt New date and time for the interview
   * @param languageId Optional language ID for the interview
   * @returns Updated interview data
   */
  async rescheduleInterview(userInterviewId: string, scheduledAt: Date | string, languageId?: string): Promise<UserInterview | null> {
    try {
      // Define a type guard function for UserInterview
      const isUserInterview = (obj: unknown): obj is UserInterview => {
        return obj !== null &&
          typeof obj === 'object' &&
          '_id' in obj &&
          'interview' in obj &&
          obj.interview !== null &&
          typeof obj.interview === 'object';
      };

      const payload = {
        status: 'scheduled',
        scheduledAt: scheduledAt instanceof Date ? scheduledAt.toISOString() : scheduledAt,
        ...(languageId && { languageId })
      };

      const response = await apiClient.patch<ApiResponse<UserInterview>>(`/user-interviews/${userInterviewId}`, payload);

      // Type guard to check if response has the expected structure
      if (response && typeof response === 'object' && 'data' in response &&
        response.data && typeof response.data === 'object' && 'data' in response.data) {
        const interview = response.data.data;
        if (isUserInterview(interview)) {
          return interview;
        }
      }

      return null;
    } catch (error) {
      console.error('Error rescheduling interview:', error);
      throw error;
    }
  }

  /**
   * Cancel a scheduled interview
   * @param userInterviewId ID of the user interview to cancel
   * @returns Cancellation status
   */
  async cancelInterview(userInterviewId: string): Promise<any> {
    try {
      const response = await apiClient.patch<ApiResponse<any>>(`/user-interviews/${userInterviewId}`, {
        status: 'cancelled'
      });

      // Check if the response contains information about calendar event removal
      if (response && response.data) {
        // If there's metadata with calendarEventRemoved flag, show additional message
        if (response.data.metadata?.calendarEventRemoved) {
          console.log('Interview cancelled and removed from Google Calendar');
          return {
            ...response.data,
            calendarEventRemoved: true
          };
        }
        return response.data;
      }

      return null;
    } catch (error) {
      console.error('Error cancelling interview:', error);
      throw error;
    }
  }

  /**
  * Complete an interview when the user has finished the interview
  * @param userInterviewId ID of the user interview to complete
  * @returns Completion status
  */
  async completeInterview(userInterviewId: string, seriesId: string | undefined, minutesUsed?: number, feedback?: InterviewFeedback): Promise<boolean> {
    try {

      console.log('[interviewService] Completing interview with userInterviewId:', userInterviewId);
      let payload: any = {
        status: 'completed',
      };

      if (seriesId) {
        payload['series'] = seriesId;
      }

      if (minutesUsed) {
        payload['minutesUsed'] = minutesUsed
      }

      if (feedback) {
        console.log('[interviewService] Feedback provided to be saved:', feedback);
        payload['feedback'] = feedback;
      }

      const response = await apiClient.patch<ApiResponse<any>>(`/user-interviews/${userInterviewId}`, payload);
      console.log('[interviewService] Received interview response:', response);
      if (response) {
        // Clear history cache when interview is completed so it shows up in history
        this.clearInterviewHistoryCache();
        return true;
      }

      return false;
    } catch (error) {
      console.error('Error completing interview:', error);
      throw error;
    }
  }


  /**
   * Join an interview (update status to 'in_progress')
   * @param interviewId Interview ID
   * @param languageId Optional language ID for the interview
   * @returns Started interview data
   */
  async joinInterview(interviewId: string, languageId?: string): Promise<any> {
    try {
      const payload = {
        status: 'in_progress',
        languageId
      };

      const response = await apiClient.patch<ApiResponse<any>>(`/user-interviews/${interviewId}`, payload);

      if (response && response.data) {
        // Clear history cache when interview is started so it shows up correctly in history
        this.clearInterviewHistoryCache();
        return response.data;
      }

      return null;
    } catch (error) {
      console.error('Error joining interview:', error);
      throw error;
    }
  }

  /**
   * Update the status of an interview
   * @param interviewId ID of the interview to update
   * @param status New status for the interview
   * @returns Updated interview data
   */
  async updateInterviewStatus(interviewId: string, status: string): Promise<any> {
    try {
      const payload = {
        status: status,
      };

      const response = await apiClient.patch<ApiResponse<any>>(`/user-interviews/${interviewId}`, payload);

      if (response && response.data) {
        // Clear history cache when interview status is updated so it shows up correctly
        this.clearInterviewHistoryCache();
        return response.data;
      }

      return null;
    } catch (error) {
      console.error('Error updating interview status:', error);
      throw error;
    }
  }

  /**
   * Get interviews by category with caching
   * @param category Category to filter by
   * @param page Page number
   * @param limit Items per page
   * @param sort Sort field
   * @param order Sort order
   * @returns List of interviews
   */
  async getInterviewsByCategory(
    category: string,
    page = 1,
    limit = 20,
    sort = 'createdAt',
    order = 'desc'
  ): Promise<Interview[]> {
    try {
      // Define a type guard function for Interview
      const isInterview = (obj: unknown): obj is Interview => {
        return obj !== null &&
          typeof obj === 'object' &&
          '_id' in obj;
      };

      const isInterviewArray = (obj: unknown): obj is Interview[] => {
        return Array.isArray(obj) &&
          (obj.length === 0 || isInterview(obj[0]));
      };

      const cacheKey = `interviews_${category}_${page}_${limit}_${sort}_${order}`;
      const cachedData = this.cache.get(cacheKey);

      if (cachedData && isInterviewArray(cachedData)) {
        return cachedData;
      }

      const response = await apiClient.get<ApiResponse<Interview[]>>(`/interviews`, {
        params: {
          category,
          page,
          limit,
          sort,
          order
        }
      });

      // Type guard to check if response has the expected structure
      if (response && typeof response === 'object' && 'data' in response &&
        response.data && typeof response.data === 'object' && 'data' in response.data) {
        const interviews = response.data.data;
        if (isInterviewArray(interviews)) {
          this.cache.set(cacheKey, interviews, 5 * 60); // Cache for 5 minutes
          return interviews;
        }
      }

      return [];
    } catch (error) {
      console.error('Error fetching interviews by category:', error);
      return [];
    }
  }

  /**
   * Get a specific interview by ID with caching
   * @param interviewId Interview ID
   * @returns Interview details or null if not found
   */
  async getInterviewById(interviewId: string): Promise<Interview | null> {
    try {
      const cacheKey = `interview_${interviewId}`;
      const cachedData = this.cache.get(cacheKey);

      // Define a type guard function for Interview
      const isInterview = (obj: unknown): obj is Interview => {
        return obj !== null &&
          typeof obj === 'object' &&
          '_id' in obj;
      };

      if (cachedData && isInterview(cachedData)) {
        return cachedData;
      }

      const response = await apiClient.get<ApiResponse<Interview>>(`/interviews/${interviewId}`);

      // Type guard to check if response has the expected structure
      if (response && typeof response === 'object' && 'data' in response &&
        response.data && typeof response.data === 'object' && 'data' in response.data) {
        const interview = response.data.data;
        if (isInterview(interview)) {
          this.cache.set(cacheKey, interview, 5 * 60); // Cache for 5 minutes
          return interview;
        }
      }

      return null;
    } catch (error) {
      console.error(`Error fetching interview with ID ${interviewId}:`, error);
      return null;
    }
  }

  /**
   * Get user's enrolled interview series
   * @returns List of enrolled series with their interviews in the format needed by InterviewSeriesSection
   */
  async getUserEnrolledSeries(): Promise<Series[]> {
    try {
      const response = await apiClient.get<any>('/user-interviews/me/series');

      let seriesData: Series[] = [];

      // Handle the case where the API returns an object with numeric keys
      if (response && typeof response === 'object' && !Array.isArray(response)) {
        seriesData = Object.values(response).filter(
          (item: any): item is Series => typeof item === 'object' && item !== null && item._id
        );
      } else if (Array.isArray(response)) {
        // Handle the case where the API returns a direct array
        seriesData = response;
      } else {
        console.warn('getUserEnrolledSeries: No series data found or format is incorrect.');
        return [];
      }
      
      const processedSeries = seriesData.map(series => ({
        ...series,
        userInterviews: series.userInterviews || [],
      }));

      console.log(`[interviewService] Fetched and processed ${processedSeries.length} enrolled series.`);
      return processedSeries;
      
    } catch (error) {
      console.error('Error fetching user enrolled series:', error);
      return [];
    }
  }

  /**
   * Get interview history from user interviews
   * @returns List of historical interviews sorted by date (most recent first)
   */
  async getUserInterviewHistory(fromDate?: string, toDate?: string): Promise<UserInterview[]> {
    if (!fromDate) {
      // Set fromDate to 6 months ago to include more history including custom interviews
      const sixMonthsAgo = new Date();
      sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
      fromDate = sixMonthsAgo.toISOString();
    }
    if (!toDate) {
      // Set toDate to current date plus 1 day to include today's interviews
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      toDate = tomorrow.toISOString();
    }

    // Generate a cache key based on date range
    const cacheKey = `user_interview_history_${fromDate}_${toDate}`;
    const cachedData = this.cache.get(cacheKey) as UserInterview[] | null;
    if (cachedData) return cachedData;

    const inFlight = this.cache.getInFlightRequest(cacheKey) as Promise<UserInterview[]> | null;
    if (inFlight) return inFlight;

    const requestPromise = this.getUserInterviews(['not_started', 'in_progress', 'completed', 'cancelled', 'expired', 'scheduled'], fromDate, toDate, false)
      .then(interviews => {
        this.cache.set(cacheKey, interviews, 5 * 60); // Cache for 5 minutes
        return interviews;
      })
      .catch(error => {
        console.error('Error fetching user interview history:', error);
        this.cache.delete(cacheKey);
        throw error;
      });
    return this.cache.setInFlightRequest(cacheKey, requestPromise);
  }

  /**
   * Get user-specific series details including progress data
   * @param seriesId Series ID
   * @returns Series details with user progress or null if not enrolled
   */
  async getUserSeriesDetails(seriesId: string): Promise<any> {
    try {
      const response = await apiClient.get<any>(`/user-interviews/me/series/${seriesId}`);

      console.log('interviewService: User series details:', response);

      // Handle different response formats
      if (response && typeof response === 'object') {
        // Handle case where api returns the whole object with data property
        if ('data' in response && response.data) {
          return response.data;
        }

        return response;
      }

      return null;
    } catch (error) {
      console.error('Error fetching user series details:', error);
      return null;
    }
  }

  /**
   * Start an async job for interview recommendations (returns jobId)
   */
  async startAsyncInterviewRecommendationJob(query: ExtendedRecommendQueryDto): Promise<{ jobId: string }> {
    const requestBody: RecommendQueryDto = {
      ...query,
      itemTypeToRecommend: RecommendableEntityType.INTERVIEW,
    };
    return apiClient.post<{ jobId: string }>('/recommendations/interviews/async', requestBody);
  }

  /**
   * Poll for async interview recommendation job status/result
   */
  async pollAsyncInterviewRecommendationJob(jobId: string): Promise<{ status: string, result?: RecommendedItem[], error?: string }> {
    if (!jobId || jobId === 'undefined' || jobId === 'null') {
      throw new Error('Invalid jobId provided for polling. A new job needs to be created first.');
    }
    return apiClient.get<{ status: string, result?: RecommendedItem[], error?: string }>(`/recommendations/interviews/async/${jobId}`);
  }

  /**
   * Create a custom interview template (not a user interview session)
   */
  async createCustomInterview(customData: CustomInterviewFormData): Promise<any> {
    try {
      console.log('🚀 createCustomInterview called with data:', customData);
      
      // First check if user can create custom interviews
      const usage = await this.getCustomInterviewUsage();
      
      if (!usage.canCreateMore) {
        const error = new Error('Custom interview limit exceeded') as any;
        error.code = 'CUSTOM_INTERVIEW_LIMIT_EXCEEDED';
        error.details = usage;
        throw error;
      }

      const payload = {
        jobRole: customData.jobRole,
        company: customData.company,
        roundType: customData.roundType.toLowerCase().includes('other') ? customData.customRoundType : customData.roundType,
        jobDescription: customData.jobDescription,
        language: customData.language,
        difficulty: customData.difficulty,
        durationMinutes: 30 // Default duration
      };

      console.log('📡 Sending payload to custom interviews API:', payload);

      // Create custom interview template first
      const response = await apiClient.post<any>('/custom-interviews', payload);

      console.log('📥 Raw API response from custom interviews:', response);

      // Handle different response formats
      if (response?.data) {
        console.log('✅ Custom interview created:', response.data);
        return response.data;
      } else if (response && typeof response === 'object' && '_id' in response) {
        console.log('✅ Direct custom interview object received:', response);
        return response;
      } else {
        console.warn('⚠️ No valid data in response, returning null');
        return null;
      }
    } catch (error) {
      console.error('❌ Error creating custom interview:', error);
      throw error;
    }
  }


  /**
   * Get user's custom interview
   * @returns List of custom interview templates created by the user
   */
  async getMyCustomInterviewTemplates(): Promise<any[]> {
    try {
      console.log('🔍 Fetching custom interview templates...');
      
      const response = await apiClient.get<any>('/custom-interviews/me');
      console.log('📡 Raw API response for custom interview templates:', response);

      let templates: any[] = [];

      // Handle different response formats
      if (response?.data) {
        if (Array.isArray(response.data)) {
          templates = response.data;
        } else if (typeof response.data === 'object' && 'data' in response.data && Array.isArray(response.data.data)) {
          templates = response.data.data;
        }
      } else if (Array.isArray(response)) {
        templates = response;
      }

      console.log(`📊 Found ${templates.length} custom interview templates`);
      return templates;
    } catch (error) {
      console.error('❌ Error fetching custom interview templates:', error);
      return [];
    }
  }

  /**
   * Get user's custom interviews (converts templates to display format for UI compatibility)
   * @returns List of custom interviews in UserInterview format for UI display
   */
  async getMyCustomInterviews(): Promise<UserInterview[]> {
    try {
      console.log('🔍 Fetching custom interviews for UI display...');
      
      // Get templates from custom-interviews endpoint
      const templates = await this.getMyCustomInterviewTemplates();
      
      // Convert templates to UserInterview format for UI compatibility
      const customInterviews: UserInterview[] = templates.map(template => ({
        _id: template._id,
        user: template.createdBy,
        interview: {
          _id: template._id,
          title: template.title,
          description: `${template.roundType} interview for ${template.jobRole} position`,
          company: template.company,
          role: template.jobRole,
          tags: [template.roundType, template.company, template.jobRole],
          durationMinutes: template.durationMinutes,
          difficulty: template.difficulty,
          features: ['Custom Interview', 'AI-Powered', template.roundType],
          isActive: template.isActive,
          voicePack: template.voicePack,
          characterGender: template.characterGender,
          interviewPrompt: template.interviewPrompt,
          createdAt: template.createdAt,
          updatedAt: template.updatedAt,
          isCustom: true,
          jobDescription: template.jobDescription,
          language: template.language,
          roundType: template.roundType
        },
        status: 'not_started' as const,
        scheduledAt: template.createdAt,
        minutesUsed: 0,
        feedback: undefined,
        createdAt: template.createdAt,
        updatedAt: template.updatedAt,
        isCustomInterview: true
      }));

      console.log(`🎯 Converted ${customInterviews.length} templates to UI format`);
      return customInterviews;
    } catch (error) {
      console.error('❌ Error fetching custom interviews:', error);
      return [];
    }
  }

  /**
   * Get custom interview usage statistics for the current user
   * @returns Usage statistics including limits and current count
   */
  async getCustomInterviewUsage(): Promise<{
    currentMonthCount: number;
    monthlyLimit: number;
    canCreateMore: boolean;
    userPlan: string;
    isFreePlan: boolean;
    resetDate: string;
  }> {
    try {
      const response = await apiClient.get<any>('/user-interviews/me/custom-interview-usage');

      // Handle different response formats
      if (response?.data) {
        return response.data;
      } else if (response && typeof response === 'object') {
        return response as any;
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('Error fetching custom interview usage:', error);
      throw error;
    }
  }

  /**
   * Check if user can create custom interviews and show upgrade prompt if needed
   * @returns Promise that resolves if user can create, rejects with error details if not
   */
  async checkCustomInterviewLimits(): Promise<void> {
    const usage = await this.getCustomInterviewUsage();
    
    if (!usage.canCreateMore) {
      const error = new Error('Custom interview limit exceeded') as any;
      error.code = 'CUSTOM_INTERVIEW_LIMIT_EXCEEDED';
      error.details = usage;
      throw error;
    }
  }

  /**
   * Generate interview form data using AI based on a short description
   */
  async generateInterviewFormData(description: string): Promise<{
    jobRole: string;
    company: string;
    interviewRoundType: string;
    jobDescription: string;
    language: string;
    difficulty: number;
  }> {
    try {
      console.log('[InterviewService] Generating form data for:', description);
      
      const response = await apiClient.post<ApiResponse<{
        jobRole: string;
        company: string;
        interviewRoundType: string;
        jobDescription: string;
        language: string;
        difficulty: number;
      }>>('/user-interviews/generate-form-data', {
        description: description.trim()
      });

      console.log('[InterviewService] Raw response:', response);
      console.log('[InterviewService] Response type:', typeof response);
      console.log('[InterviewService] Response keys:', response ? Object.keys(response) : 'null');

      // Handle direct response from backend (not wrapped in ApiResponse)
      if (response && typeof response === 'object') {
        // Check if response has the form data directly
        if ('jobRole' in response && 'company' in response && 'interviewRoundType' in response && 'jobDescription' in response && 'language' in response && 'difficulty' in response) {
          console.log('[InterviewService] Direct form data response found');
          return {
            jobRole: String(response.jobRole),
            company: String(response.company),
            interviewRoundType: String(response.interviewRoundType),
            jobDescription: String(response.jobDescription),
            language: String(response.language),
            difficulty: Number(response.difficulty) || 3
          };
        }

        // Check if response has data property with ApiResponse structure
        if ('data' in response && response.data) {
          const apiResponse = response.data as any;
          console.log('[InterviewService] ApiResponse data:', apiResponse);
          console.log('[InterviewService] ApiResponse statusCode:', apiResponse?.statusCode);
          
          if (apiResponse?.statusCode === 200 && apiResponse?.data) {
            const formData = apiResponse.data;
            console.log('[InterviewService] Successfully generated form data:', formData);
            return formData;
          }
          
          // Sometimes the data might be directly in response.data without statusCode wrapper
          if ('jobRole' in apiResponse && 'company' in apiResponse) {
            console.log('[InterviewService] Form data found directly in response.data');
            return apiResponse;
          }
        }
      }
      
      console.error('[InterviewService] Unexpected response structure:', response);
      throw new Error('Invalid response format from server');
    } catch (error: any) {
      console.error('[InterviewService] Error generating form data:', error);
      
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else if (error.message) {
        throw error;
      } else {
        throw new Error('Failed to generate interview form data');
      }
    }
  }

  /**
   * Start a custom interview session (creates a user interview from custom interview template)
   */
  async startCustomInterviewSession(customInterviewId: string, scheduledAt?: Date | string, languageId?: string): Promise<UserInterview | null> {
    try {
      console.log('🚀 Starting custom interview session for custom interview:', customInterviewId);
      
      // Define a type guard function for UserInterview
      const isUserInterview = (obj: unknown): obj is UserInterview => {
        return obj !== null &&
          typeof obj === 'object' &&
          '_id' in obj &&
          'interview' in obj &&
          obj.interview !== null &&
          typeof obj.interview === 'object';
      };
      
      const payload = {
        isCustomInterview: true,
        customInterview: customInterviewId,
        scheduledAt: scheduledAt,
        languageId: languageId
      };

      console.log('📡 Sending payload to user interviews API:', payload);

      const response = await apiClient.post<ApiResponse<UserInterview> | UserInterview>('/user-interviews', payload);

      console.log('📥 Raw API response for custom interview session:', response);

      // Check if response is direct UserInterview object (new format)
      if (response && typeof response === 'object' && '_id' in response && 'interview' in response) {
        console.log('✅ Direct custom interview session object received');
        if (isUserInterview(response)) {
          return response;
        }
      }

      // Handle wrapped format (ApiResponse)
      if (response && typeof response === 'object' && 'data' in response &&
        response.data && typeof response.data === 'object' && 'data' in response.data) {
        console.log('✅ ApiResponse wrapped custom interview session received');
        const interview = response.data.data;
        if (isUserInterview(interview)) {
          return interview;
        }
      }

      console.error('❌ Unexpected API response format:', response);
      return null;
    } catch (error: any) {
      console.error('❌ Error starting custom interview session:', error);
      
      if (error.response?.data) {
        console.error('❌ Error response data:', error.response.data);
        throw new Error(error.response.data.message || 'Failed to start custom interview session');
      } else {
        throw new Error('Failed to start custom interview session');
      }
    }
  }

  /**
   * Update user feedback rating for an interview
   * @param userInterviewId ID of the user interview to update
   * @param rating 'like' or 'dislike'
   * @returns Updated interview data
   */
  async updateUserFeedbackRating(userInterviewId: string, rating: 'like' | 'dislike'): Promise<UserInterview | null> {
    try {
      const payload = {
        userFeedbackRating: rating
      };

      const response = await apiClient.patch<ApiResponse<UserInterview>>(`/user-interviews/${userInterviewId}`, payload);
      // Type guard to check if response has the expected structure
      if (response) {
        return response as unknown as UserInterview;
      }

      return null;
    } catch (error) {
      console.error('Error updating user feedback rating:', error);
      throw error;
    }
  }
}

// Export service as singleton
const interviewService = new InterviewService();
export default interviewService;